import {
  DEFAULT_ACTIVITY_END_TIME,
  DEFAULT_ACTIVITY_START_TIME,
  DEFAULT_ACTIVITY_TIME_SPAN,
  DEFAULT_ATTRACTION_END_TIME,
  DEFAULT_ATTRACTION_START_TIME,
  DEFAULT_ATTRACTION_TIME_SPAN,
  DEFAULT_CAR_RENTAL_TIME_SPAN,
  DEFAULT_CUSTOM_END_TIME,
  DEFAULT_CUSTOM_START_TIME,
  DEFAULT_EVENT_DURATION_HOURS,
  DEFAULT_EVENT_END_TIME,
  DEFAULT_EVENT_START_TIME,
  DEFAULT_HOTEL_CHECKIN_TIME,
  DEFAULT_HOTEL_CHECKOUT_TIME,
  DEFAULT_HOTEL_TIME_SPAN,
  DEFAULT_LOCATION_END_TIME,
  DEFAULT_LOCATION_START_TIME,
  DEFAULT_LOCATION_TIME_SPAN,
  DEFAULT_OTHER_TIME_SPAN,
  DEFAULT_RENTAL_CAR_CHECKIN_TIME,
  DEFAULT_RENTAL_CAR_CHECKOUT_TIME,
  DEFAULT_RESTAURANT_DINNER_END_TIME,
  DEFAULT_RESTAURANT_DINNER_START_TIME,
  DEFAULT_RESTAURANT_TIME_SPAN,
} from "@/constants/itinerary"
import {
  DayObject,
  DayRange,
  END_OF_DAY,
  FixedDayRange,
  START_OF_DAY,
  TimeOfDay,
  TimeRange,
  Timestamp,
} from "@/types/date"
import { FolioDocumentRef } from "@/types/folio/folio-document"
import {
  DayNote,
  DayTitle,
  ExpandedItineraryDay,
  Itinerary,
  ItineraryEvent,
  ItineraryEventType,
  ItineraryEventWithFolioBooking,
  ItineraryEventWithMindtripFlightBooking,
  ItineraryEventWithMindtripHotelBooking,
  ItineraryItem,
  PlaceItineraryEvent,
} from "@/types/itinerary"
import { EventSchedule, Place } from "@/types/place"
import { TimeFrame } from "@/types/trip"
import { WebSearchResult } from "@/types/web-search-result"
import uniq from "lodash/uniq"

import { links } from "@/lib/routes"
import { DraggableItem } from "@/lib/storage/dragging-slice"
import { assertUnreachable } from "@/lib/utils/assertions"
import { freeze, getConstantEmptyArray } from "@/lib/utils/collections"
import {
  addDays,
  addHours,
  addMillis,
  afterDayObject,
  afterTimestamp,
  beforeDayObject,
  browserToday,
  changeDay,
  changeTimeOfDay,
  compareDayObjects,
  compareTimestamps,
  dayAfter,
  dayBefore,
  dayRangeToDayArray,
  durationInMinutes,
  getDayObject,
  getTimeOfDay,
  getTimestamp,
  millisBetween,
  nightsBetweenDayObjects,
  nowMillis,
  rangeContainsDay,
  sameDay,
  today,
} from "./date-utils"
import {
  googleDirectionsLink,
  googleDirectionsLinkFromText,
  isHotel,
} from "./entity-utils"
import { formatDayCount } from "./format-utils"
import {
  dayRangeForDraftDates,
  isTimeFrameCurrentlyActive,
} from "./time-frame-utils"

const EVENT_COLOR_PAIRS = [
  "bg-amber-4 text-amber-12",
  "bg-lime-4 text-lime-12",
  "bg-mint-4 text-mint-12",
  "bg-violet-4 text-violet-12",
  "bg-crimson-4 text-crimson-12",
  "bg-cyan-4 text-cyan-12",
  "bg-gold-4 text-gold-12",
  "bg-indigo-4 text-indigo-12",
  "bg-plum-4 text-plum-12",
  "bg-sky-4 text-sky-12",
  "bg-teal-4 text-teal-12",
  "bg-orange-4 text-orange-12",
]

const hashString = (str: string) => {
  let hash = 0
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i)
    hash = (hash << 5) - hash + char
    hash |= 0
  }
  return Math.abs(hash)
}

export function eventColorPair(eventName: string) {
  const colorIndex = hashString(eventName) % EVENT_COLOR_PAIRS.length
  return EVENT_COLOR_PAIRS[colorIndex]
}

export function getAllEventTypes() {
  if (!_allEventTypes)
    _allEventTypes = freeze([
      "restaurant",
      "cafe",
      "hotel",
      "camp",
      "flight",
      "airport",
      "activity",
      "attraction",
      "experience",
      "event",
      "location",
      "car_rental",
      "transit",
      "generic",
    ])
  return _allEventTypes
}

let _allEventTypes: ItineraryEventType[] | undefined

// True for any event that is a flight event -- whether found through
// Mindtrip or just entered manually.
export function isFlightEvent(event: ItineraryEvent): boolean {
  return event.type === "flight"
}

export function departureAirportFromEvent(event: ItineraryEvent | undefined) {
  return event?.type === "flight" && event.place?.type === "airport"
    ? event.place
    : undefined
}

export function arrivalAirportFromEvent(event: ItineraryEvent | undefined) {
  return event?.type === "flight" && event.end_place?.type === "airport"
    ? event.end_place
    : undefined
}

// True for any event that is a hotel event -- whether found through
// Mindtrip or just entered manually.
export function isHotelEvent(event: ItineraryEvent): boolean {
  return event.type === "hotel"
}

export function isNezasaEvent(event: ItineraryEvent): boolean {
  return (
    event.properties.nezasa_props?.id !== undefined &&
    event.properties.nezasa_props?.id !== ""
  )
}

export function isCarRentalEvent(event: ItineraryEvent): boolean {
  return event.type === "car_rental"
}

// TODO: maybe what callers really want to know is whether this is a
// multi-day event with specific start and end times, like a hotel
// check-in or rental car pickup but potentially something else, like
// a stay at a campsite or anywhere where we'd show it spanning days
// like an all-day event but also show the specific check-in and -out
// times on the first and last days.
//
// But we'd need to watch out for a late-night event that drifts
// past midnight a little and probably should not get this treatment, even
// though it might look like a multi-day event.
export function isHotelOrCarRentalEvent(event: ItineraryEvent): boolean {
  return isHotelEvent(event) || isCarRentalEvent(event)
}

// True for saved flights (whether they are booked or not).
export function isEventWithMindtripFlightBooking(
  event: ItineraryEvent
): event is ItineraryEventWithMindtripFlightBooking {
  return (
    event.type === "flight" && !!event.flight_itinerary && !!event.flight_leg
  )
}

export function isEventWithMindtripHotelBooking(
  event: ItineraryEvent
): event is ItineraryEventWithMindtripHotelBooking {
  return event.type === "hotel" && !!event.hotel_booking
}

/** Made by a browser agent booking on a provider's site (Book Anything). */
export function isBrowserBookedEvent(event: ItineraryEvent) {
  return event.booking?.source_type === "BrowserBooking"
}

/** The traveler's own entry: nothing booked it, no receipt stands behind it. */
export function isTravelerEntry(event: ItineraryEvent) {
  return !event.booking && !event.flight_itinerary && !event.hotel_booking
}

/**
 * A booked event the traveler can take off the trip: their own entry, or a receipt's event
 * (uploaded or browser-booked), which the server unassigns from the trip rather than deleting.
 * Mindtrip hotel and flight bookings are cancelled instead.
 */
export function isRemovableBooking(event: ItineraryEvent) {
  return (
    isTravelerEntry(event) ||
    isFolioItineraryEvent(event) ||
    isBrowserBookedEvent(event)
  )
}

/** The uploaded receipt an event came from, when it did. */
export function folioRef(event: ItineraryEvent): FolioDocumentRef | undefined {
  return isFolioItineraryEvent(event) ? event.booking.source_ref : undefined
}

export function flightItinerary(event: ItineraryEvent) {
  return isEventWithMindtripFlightBooking(event)
    ? event.flight_itinerary
    : undefined
}

export function flightSlice(event: ItineraryEvent) {
  const fi = flightItinerary(event)
  const fl = flightLeg(event)
  if (!fi || !fl) return

  return fi.flight_slices.find((slice) =>
    slice.flight_legs.find((leg) => leg.id === fl.id)
  )
}

export function flightLeg(event: ItineraryEvent) {
  return isEventWithMindtripFlightBooking(event) ? event.flight_leg : undefined
}

export function isFolioItineraryEvent(
  event: ItineraryEvent
): event is ItineraryEventWithFolioBooking {
  return event.booking?.source_type === "FolioDocument"
}

export function isBooked(event: ItineraryEvent) {
  return event.status === "booked"
}

export function isSandbox(event: ItineraryEvent): boolean {
  const fi = flightItinerary(event)
  return !!(fi?.sandbox || fi?.flight_booking?.sandbox)
}

export function isCancelled(event: ItineraryEvent): boolean {
  return event.status === "cancelled"
}

export function isCancelledBooking(event: ItineraryEvent): boolean {
  return isCancelled(event) && !!(event.hotel_booking || event.flight_itinerary)
}

export interface HotelBookingAction {
  label: "Manage booking" | "View booking"
  url: string | undefined
}

export function hotelBookingActionForEvent(
  event: ItineraryEvent
): HotelBookingAction | undefined {
  const hotelBooking = event.hotel_booking
  if (!hotelBooking) return undefined

  if (hotelBooking.provider_name === "nuitee") {
    return {
      label: "Manage booking",
      url: hotelBooking.manage_booking_url ?? undefined,
    }
  }

  return {
    label: "View booking",
    url: hotelBooking.ref
      ? links.hotelBookingDetails(hotelBooking.ref)
      : undefined,
  }
}

export function isPendingFlightBookingEvent(event: ItineraryEvent): boolean {
  return (
    isEventWithMindtripFlightBooking(event) &&
    event.flight_itinerary.flight_booking?.status === "pending-confirm"
  )
}

export function isAutoGenerated(event: ItineraryEvent) {
  return event.bot_generated || !event.id
  // TODO: kind of cheating here to assume an undefined id means
  // it's an event that's currently being streamed. Maybe clean up
  // the typescript interfaces around this.
}

// Can this itinerary event be moved to a different day or time?
// TODO: use flag from the server instead of duplicating logic. See ItineraryEvent#moveable?
export function isEditable(event: ItineraryEvent): boolean {
  return event.read_only !== true
}

export function isMoveable(event: ItineraryEvent): boolean {
  return isEditable(event) && !isNezasaEvent(event)
}

export function isCustomEvent(event: ItineraryEvent): boolean {
  return (
    !event.place ||
    (event.type === "flight" && !isEventWithMindtripFlightBooking(event))
  )
}

export function isPlaceItineraryEvent(
  event: ItineraryEvent
): event is PlaceItineraryEvent {
  return !!event.place
}

// True if this event is displayed as a single item in
// the itinerary or calendar.
export function isSingleItemEvent(event: ItineraryEvent): boolean {
  return !isMultiItemEvent(event)
}

// True if this event is displayed as multiple items in
// the itinerary or calendar (e.g. if its start_at is
// shown as a check-in item and its end_at is shown as
// a check-out item). We don't do this with flight events
// because it's confusing to show two blocks for a single
// flight leg, even if it spans days -- unless the user has
// set the custom flight event to be all-day. An event whose
// local end date precedes its local start date is also shown as
// one start-anchored item because its endpoint dates don't form
// a chronological visual span. Stays are the exception because
// their check-in and check-out blocks remain independently useful.
export function isMultiItemEvent(event: ItineraryEvent): boolean {
  const isStay = event.type === "hotel" || event.type === "camp"
  return (
    (isStay || !hasReversedLocalDateOrder(event)) &&
    (event.type !== "flight" || event.all_day) &&
    !sameDay(startDay(event), endDay(event))
  )
}

export function formatItineraryEventDurationLabel({
  locale,
  durationMinutes,
  localDayDifference,
  allDay,
  eventType,
}: {
  locale: string
  durationMinutes: number
  localDayDifference: number
  allDay: boolean
  eventType: ItineraryEventType
}): string | undefined {
  const sign = durationMinutes < 0 ? "-" : ""
  const absoluteMinutes = Math.abs(durationMinutes)
  const hours = Math.floor(absoluteMinutes / 60)
  const minuteRemainder = absoluteMinutes % 60
  const durationLabel =
    hours === 0
      ? `${sign}${minuteRemainder}m`
      : minuteRemainder === 0
        ? `${sign}${hours}h`
        : `${sign}${hours}h${minuteRemainder}m`

  const isStay = eventType === "hotel" || eventType === "camp"
  if (isStay && localDayDifference > 0) {
    return `${localDayDifference} ${localDayDifference === 1 ? "night" : "nights"}`
  } else if (!allDay) {
    // The editor already shows both endpoint dates. Keep their calendar
    // relationship out of the elapsed duration label.
    return durationLabel
  } else if (localDayDifference > 0) {
    return formatDayCount(locale, localDayDifference + 1)
  }
}

// Returns the lat/long where the given event starts, if known
export function eventLatLong(
  event: ItineraryEvent
): [number, number] | undefined {
  if (event.properties.latitude != null && event.properties.longitude != null) {
    return [event.properties.latitude, event.properties.longitude]
  } else if (event.place?.latitude != null && event.place?.longitude != null) {
    return [event.place.latitude, event.place.longitude]
  }
  return undefined
}

// Returns the lat/long where the given event ends if known. This
// will usually be the same as eventLatLong() (the place where the
// event starts), but some events -- like a flight -- may end in
// a different place from where they start.
export function eventEndLatLong(
  event: ItineraryEvent
): [number, number] | undefined {
  if (
    event.properties.latitude !== undefined &&
    event.properties.longitude !== undefined
  ) {
    return [event.properties.latitude, event.properties.longitude]
  } else if (
    event.end_place &&
    event.end_place.latitude !== undefined &&
    event.end_place.longitude !== undefined
  ) {
    return [event.end_place.latitude, event.end_place.longitude]
  } else if (
    event.place &&
    event.place.latitude !== undefined &&
    event.place.longitude !== undefined
  ) {
    return [event.place.latitude, event.place.longitude]
  }

  return undefined
}

export function compareStartTimes(
  event1: ItineraryEvent,
  event2: ItineraryEvent
): number {
  return compareTimestamps(event1.start_at, event2.start_at)
}

export function startDay(event: ItineraryEvent): DayObject {
  return getDayObject(event.start_at, event.time_zone)
}

export function endDay(event: ItineraryEvent): DayObject {
  return getDayObject(event.end_at, event.end_time_zone)
}

/**
 * Applies itinerary-item movement semantics at a target local day and time.
 * Single items move as one interval; multi-item rows move only the endpoint
 * represented by that row.
 */
export function calcStartEndChangesForItineraryItemMove(
  event: ItineraryEvent,
  itemType: ItineraryItem["type"],
  {
    targetDay,
    targetTimeOfDay,
    clampSingleDayEndToTargetDay = false,
  }: {
    targetDay: DayObject
    targetTimeOfDay?: TimeOfDay
    clampSingleDayEndToTargetDay?: boolean
  }
): { start_at?: Timestamp; end_at?: Timestamp } {
  const moveTimestamp = (timestamp: Timestamp, timeZone: string) => {
    const timestampOnTargetDay = changeDay(timestamp, targetDay, timeZone)
    return targetTimeOfDay
      ? changeTimeOfDay(timestampOnTargetDay, targetTimeOfDay, timeZone)
      : timestampOnTargetDay
  }

  if (itemType === "multi-day-start") {
    return { start_at: moveTimestamp(event.start_at, event.time_zone) }
  } else if (itemType === "multi-day-end") {
    return { end_at: moveTimestamp(event.end_at, event.end_time_zone) }
  } else if (itemType === "single-day") {
    const startAt = moveTimestamp(event.start_at, event.time_zone)
    let endAt = addMillis(startAt, millisBetween(event.start_at, event.end_at))
    if (clampSingleDayEndToTargetDay) {
      const latestEnd = getTimestamp(targetDay, END_OF_DAY, event.time_zone)
      if (afterTimestamp(endAt, latestEnd)) {
        endAt = latestEnd
      }
    }
    return { start_at: startAt, end_at: endAt }
  }

  return assertUnreachable(itemType)
}

export function calcStartEndChangesForItineraryItemDayMove(
  event: ItineraryEvent,
  itemType: ItineraryItem["type"],
  dayOffset: number
): { start_at?: Timestamp; end_at?: Timestamp } {
  const itemDay = itemType === "multi-day-end" ? endDay(event) : startDay(event)
  return calcStartEndChangesForItineraryItemMove(event, itemType, {
    targetDay: addDays(itemDay, dayOffset),
  })
}

export function dayRangeForEvent(event: ItineraryEvent): FixedDayRange {
  const eventStartDay = startDay(event)
  const eventEndDay = endDay(event)
  return compareDayObjects(eventStartDay, eventEndDay) <= 0
    ? { from: eventStartDay, to: eventEndDay }
    : { from: eventEndDay, to: eventStartDay }
}

export function shouldShowExplicitTimeZoneForItineraryEvent(
  event: ItineraryEvent
): boolean {
  return (
    event.time_zone !== event.end_time_zone ||
    !sameDay(startDay(event), endDay(event))
  )
}

export function hasReversedLocalDateOrder(event: ItineraryEvent): boolean {
  return compareDayObjects(startDay(event), endDay(event)) > 0
}

export function timeOfDayForEvent(
  event: ItineraryEvent,
  startOrEnd: "start" | "end"
) {
  return getTimeOfDay(
    startOrEnd === "start" ? event.start_at : event.end_at,
    event.end_time_zone
  )
}

export function isItineraryInDayRange(
  itinerary: Itinerary,
  dayRange: DayRange
) {
  const results = itinerary.events.map((event) => {
    const eventDayRange = dayRangeForEvent(event)
    const invalid =
      compareDayObjects(eventDayRange.from, dayRange.from) <= -1 ||
      compareDayObjects(eventDayRange.to, dayRange.to) >= 1
    return !invalid
  })
  return results.every((v) => v === true)
}

export function isFutureTrip(startDate: DayObject) {
  return beforeDayObject(browserToday(), startDate)
}

export function supportsAllDay(event: ItineraryEvent): boolean {
  return (
    event.type !== "hotel" &&
    event.type !== "car_rental" &&
    event.type !== "flight"
  )
}

export function defaultStartAndEndTimeForCustom(
  start: DayObject,
  end: DayObject | undefined,
  timeZone: string
): TimeRange {
  return {
    from: defaultCustomStartTime(start, timeZone),
    to: defaultCustomEndTime(end || start, timeZone),
  }
}

export function defaultTimeRange(
  place: Place,
  start: DayObject,
  end: DayObject | null | undefined
): TimeRange {
  switch (place.type) {
    case "hotel":
      if (!end) {
        end = dayAfter(start) // Default to just 1 night for hotel
        // TODO: actually use end of trip
      }
      return {
        from: defaultHotelCheckInTimeFromPlace(place, start),
        to: defaultHotelCheckOutTimeFromPlace(place, end),
      }

    case "restaurant":
      return {
        from: defaultRestaurantLunchStartTime(start, place.time_zone),
        to: defaultRestaurantLunchEndTime(end || start, place.time_zone),
      }

    case "event":
      return {
        from: defaultEventStartTime(start, place.time_zone),
        to: defaultEventEndTime(start, place.time_zone),
      }

    case "attraction":
      return {
        from: defaultAttractionStartTime(start, place.time_zone),
        to: defaultAttractionEndTime(end || start, place.time_zone),
      }

    case "activity":
      return {
        from: defaultActivityStartTime(start, place.time_zone),
        to: defaultActivityEndTime(end || start, place.time_zone),
      }

    case "location":
      return {
        from: defaultLocationStartTime(start, place.time_zone),
        to: defaultLocationEndTime(end || start, place.time_zone),
      }

    case "airport":
      return {
        from: defaultLocationStartTime(start, place.time_zone),
        to: defaultLocationEndTime(end || start, place.time_zone),
      }

    default:
      assertUnreachable(place)
  }
}

export function defaultTimeSpan(type: ItineraryEventType, date: Timestamp) {
  switch (type) {
    case "hotel":
      return addHours(date, DEFAULT_HOTEL_TIME_SPAN)

    case "restaurant":
      return addHours(date, DEFAULT_RESTAURANT_TIME_SPAN)

    case "attraction":
      return addHours(date, DEFAULT_ATTRACTION_TIME_SPAN)

    case "activity":
      return addHours(date, DEFAULT_ACTIVITY_TIME_SPAN)

    case "event":
      return addHours(date, DEFAULT_OTHER_TIME_SPAN)

    case "location":
      return addHours(date, DEFAULT_LOCATION_TIME_SPAN)

    case "car_rental":
      return addHours(date, DEFAULT_CAR_RENTAL_TIME_SPAN)

    default:
      return addHours(date, DEFAULT_OTHER_TIME_SPAN)
  }
}

export function defaultHotelCheckInTimeFromPlace(place: Place, day: DayObject) {
  if (isHotel(place) && place.check_in) {
    return getTimestamp(day, place.check_in, place.time_zone)
  }
  return defaultHotelCheckInTime(day, place.time_zone)
}

export function defaultHotelCheckOutTimeFromPlace(
  place: Place,
  day: DayObject
) {
  if (isHotel(place) && place.check_out) {
    return getTimestamp(day, place.check_out, place.time_zone)
  }
  return defaultHotelCheckOutTime(day, place.time_zone)
}

export function defaultHotelCheckInTime(day: DayObject, timeZone: string) {
  return getTimestamp(day, DEFAULT_HOTEL_CHECKIN_TIME, timeZone)
}

export function defaultHotelCheckOutTime(day: DayObject, timeZone: string) {
  return getTimestamp(day, DEFAULT_HOTEL_CHECKOUT_TIME, timeZone)
}

export function defaultRestaurantLunchStartTime(
  day: DayObject,
  timeZone: string
) {
  return getTimestamp(day, DEFAULT_RESTAURANT_DINNER_START_TIME, timeZone)
}

export function defaultRestaurantLunchEndTime(
  day: DayObject,
  timeZone: string
) {
  return getTimestamp(day, DEFAULT_RESTAURANT_DINNER_END_TIME, timeZone)
}

// function defaultRestaurantDinnerStartTime(date: Date, timeZone: string) {
//   return createDate(
//     getDay(date, timeZone),
//     DEFAULT_RESTAURANT_DINNER_START_TIME,
//     timeZone
//   )
// }

// function defaultRestaurantDinnerEndTime(date: Date, timeZone: string) {
//   return createDate(
//     getDay(date, timeZone),
//     DEFAULT_RESTAURANT_DINNER_END_TIME,
//     timeZone
//   )
// }

export function defaultEventStartTime(day: DayObject, timeZone: string) {
  return getTimestamp(day, DEFAULT_EVENT_START_TIME, timeZone)
}

export function defaultEventEndTime(day: DayObject, timeZone: string) {
  return getTimestamp(day, DEFAULT_EVENT_END_TIME, timeZone)
}

export function defaultAttractionStartTime(day: DayObject, timeZone: string) {
  return getTimestamp(day, DEFAULT_ATTRACTION_START_TIME, timeZone)
}

export function defaultAttractionEndTime(day: DayObject, timeZone: string) {
  return getTimestamp(day, DEFAULT_ATTRACTION_END_TIME, timeZone)
}

export function defaultActivityStartTime(day: DayObject, timeZone: string) {
  return getTimestamp(day, DEFAULT_ACTIVITY_START_TIME, timeZone)
}

export function defaultActivityEndTime(day: DayObject, timeZone: string) {
  return getTimestamp(day, DEFAULT_ACTIVITY_END_TIME, timeZone)
}

export function defaultLocationStartTime(day: DayObject, timeZone: string) {
  return getTimestamp(day, DEFAULT_LOCATION_START_TIME, timeZone)
}

export function defaultLocationEndTime(day: DayObject, timeZone: string) {
  return getTimestamp(day, DEFAULT_LOCATION_END_TIME, timeZone)
}

export function defaultRentalCarCheckInTime(day: DayObject, timeZone: string) {
  return getTimestamp(day, DEFAULT_RENTAL_CAR_CHECKIN_TIME, timeZone)
}

export function defaultRentalCarCheckOutTime(day: DayObject, timeZone: string) {
  return getTimestamp(day, DEFAULT_RENTAL_CAR_CHECKOUT_TIME, timeZone)
}

// TODO: clean this up: default for a custom event should not be based
// on the fact that it's custom but based on its type, just like place
// events (hotel, restaurant, etc.)
export function defaultCustomStartTime(day: DayObject, timeZone: string) {
  return getTimestamp(day, DEFAULT_CUSTOM_START_TIME, timeZone)
}

export function defaultCustomEndTime(day: DayObject, timeZone: string) {
  return getTimestamp(day, DEFAULT_CUSTOM_END_TIME, timeZone)
}

export function defaultTimestampsForPlace(
  place: Place,
  tripDates: FixedDayRange
): { startTimestamp: Timestamp; endTimestamp: Timestamp | undefined } {
  const startDate = tripDates.from
  const endDate = tripDates.to

  const defaultStartEndTime = defaultTimeRange(
    place,
    startDate,
    isHotel(place) ? endDate : undefined
  )

  const startTime = defaultStartEndTime.from
    ? getTimeOfDay(defaultStartEndTime.from, place.time_zone)
    : START_OF_DAY

  const startTimestamp = getTimestamp(startDate, startTime, place.time_zone)
  const endTimestamp = defaultStartEndTime.to
    ? getTimestamp(
        getDayObject(defaultStartEndTime.to, place.time_zone),
        getTimeOfDay(defaultStartEndTime.to, place.time_zone),
        place.time_zone
      )
    : undefined

  return { startTimestamp, endTimestamp }
}

export function placeHasFixedSchedule(place: Place): place is Place & {
  type: "event"
  schedules: { data: EventSchedule[] }
} {
  return (
    place?.type === "event" &&
    place.schedules?.data?.length !== undefined &&
    place.schedules.data.length > 0
  )
}

export function searchResultHasFixedSchedule(
  searchResult: WebSearchResult
): boolean {
  return searchResult?.type === "event" && !!searchResult.schedule?.start_at
}

export function itineraryEventHasFixedSchedule(event: ItineraryEvent): boolean {
  if (isEventWithMindtripFlightBooking(event)) {
    return true
  }

  const searchResult = event.properties?.web_search_result
  if (searchResult && searchResultHasFixedSchedule(searchResult)) {
    return true
  }

  if (event.place && placeHasFixedSchedule(event.place)) {
    return true
  }

  return false
}

export function draggableItemHasFixedSchedule(
  item: DraggableItem
): item is DraggableItem & { hasFixedSchedule: true } {
  return (
    (item.type === "itinerary-item" ||
      item.type === "place" ||
      item.type === "search-result") &&
    !!item.hasFixedSchedule
  )
}

export function shouldShowFixedScheduleOverlay(item: DraggableItem): boolean {
  if (!draggableItemHasFixedSchedule(item)) {
    return false
  }
  return true
}

/**
 * Resolve the start/end for adding a place to a day. When an event carries its
 * own scheduled time (e.g. a concert at 7:30pm) it is preserved as-is; otherwise
 * we anchor to the day (using the day's slot times when provided) and let the
 * type-based default time range apply downstream.
 */
export function resolveAddPlaceTimeRange({
  place,
  day,
  endDay,
  startTime,
  endTime,
  timeZone,
}: {
  place: Place
  day: DayObject
  endDay?: DayObject
  startTime?: TimeOfDay
  endTime?: TimeOfDay
  timeZone: string
}): { start: Timestamp; end: Timestamp; defaultTime: boolean } {
  if (placeHasFixedSchedule(place)) {
    const schedule = place.schedules.data[0]
    return {
      start: schedule.start_at,
      end: schedule.end_at ?? addHours(schedule.start_at, 1),
      defaultTime: false,
    }
  }

  const isHotelStay = place.type === "hotel"
  // A hotel always checks out the next day. Otherwise a dragged end day
  // applies, but only to anchor the end time that came with it — the default
  // span below is same-day.
  const lastDay = isHotelStay ? dayAfter(day) : (endDay ?? day)
  const defaultEndDay = isHotelStay ? lastDay : day
  return {
    start: startTime
      ? getTimestamp(day, startTime, timeZone)
      : defaultCustomStartTime(day, timeZone),
    end: endTime
      ? getTimestamp(lastDay, endTime, timeZone)
      : defaultCustomEndTime(defaultEndDay, timeZone),
    defaultTime: true,
  }
}

/**
 * The end a calendar drag-selection should hand to the add-event dialog.
 *
 * A click lands here as a single-slot selection, indistinguishable from the
 * shortest possible drag, so anything under an hour is treated as "the user
 * didn't pick a length" and gets the same hour a form with nothing dragged
 * behind it would give. Anything longer is what they actually dragged.
 */
export function resolveCalendarSelectionEnd(
  start: Timestamp,
  end: Timestamp
): Timestamp {
  return durationInMinutes(start, end) < DEFAULT_EVENT_DURATION_HOURS * 60
    ? addHours(start, DEFAULT_EVENT_DURATION_HOURS)
    : end
}

export function computeItineraryDays(
  userTimeZone: string,
  draftStartDate: DayObject | undefined,
  draftDuration: number | undefined,
  events: ItineraryEvent[],
  dayTitles: DayTitle[],
  dayNotes: DayNote[] = []
): ExpandedItineraryDay[] {
  if (!draftStartDate || draftDuration === undefined) {
    return getConstantEmptyArray<ExpandedItineraryDay>()
  }
  const dayRange: DayRange = dayRangeForDraftDates(
    draftStartDate,
    draftDuration
  )
  const days = dayRangeToDayArray(dayRange)
  if (days.length === 0) {
    return getConstantEmptyArray<ExpandedItineraryDay>()
  }

  const hasEventsBeforeTrip = events.some((e) =>
    beforeDayObject(dayRangeForEvent(e).from, days[0])
  )
  if (hasEventsBeforeTrip) {
    // Some events are before the trip start. Add a special "before" day.
    days.unshift(dayBefore(days[0]))
  }
  const hasEventsAfterTrip = events.some((e) =>
    afterDayObject(dayRangeForEvent(e).to, days[days.length - 1])
  )
  if (hasEventsAfterTrip) {
    // Some events are after the trip end. Add a special "after" day.
    days.push(dayAfter(days[days.length - 1]))
  }

  return days.map<ExpandedItineraryDay>((day, dayIndex) => {
    const title = dayTitles.find((t) => sameDay(t.date, day))?.title
    const note = dayNotes.find((dayNote) => sameDay(dayNote.date, day))?.note
    const isFirstDay = dayIndex === 0
    const isLastDay = dayIndex === days.length - 1
    const items: ItineraryItem[] = []
    const spanningHotelEvents: ItineraryEvent[] = []
    events.forEach((event) => {
      const eventDayRange = dayRangeForEvent(event)

      const showTimestampOnItineraryDay = (
        time: Timestamp,
        timeZone: string
      ) => {
        const dayToTest = getDayObject(time, timeZone)
        return (
          sameDay(dayToTest, day) ||
          (isFirstDay && !afterDayObject(dayToTest, day)) ||
          (isLastDay && !beforeDayObject(dayToTest, day))
        )
      }

      // TODO: We used to have logic here to collapse flight itineraries
      // from multiple events if they had legs in common -- to show the
      // leg only once. Removed that when we updated the model to have
      // an itinerary event per leg. Revisit if we need it.

      if (
        isSingleItemEvent(event) &&
        showTimestampOnItineraryDay(event.start_at, event.time_zone)
      ) {
        items.push({
          type: "single-day",
          event,
          allDay: event.all_day,
          timestamp: event.start_at,
          timeZone: event.time_zone,
          flightItinerary: event.flight_itinerary || undefined,
          flightLeg: event.flight_leg,
        })
      } else if (
        isMultiItemEvent(event) &&
        showTimestampOnItineraryDay(event.start_at, event.time_zone)
      ) {
        // Add a check-in item
        items.push({
          event,
          allDay: event.all_day,
          timestamp: event.start_at,
          timeZone: event.time_zone,
          type: "multi-day-start",
          nights: nightsBetweenDayObjects(eventDayRange.from, eventDayRange.to),
        })
      } else if (
        isMultiItemEvent(event) &&
        showTimestampOnItineraryDay(event.end_at, event.end_time_zone)
      ) {
        // Add a check-out item
        items.push({
          event,
          allDay: event.all_day,
          timestamp: event.end_at,
          timeZone: event.end_time_zone,
          type: "multi-day-end",
        })

        if (event.type === "hotel") {
          spanningHotelEvents.push(event)
        }
        // For a hotel stay, we include two ItineraryItems in the
        // ItineraryDay's list of items: one for the check-in and
        // one for the check-out. But separately, to allow us to
        // show hotel stays on in-between days, we also collect
        // the hotel events in a separate list per ItineraryDay.
      } else if (
        event.type === "hotel" &&
        rangeContainsDay(dayRangeForEvent(event), day)
      ) {
        spanningHotelEvents.push(event)
      }
    })

    items.sort((a, b) => {
      if (a.allDay !== b.allDay) {
        return a.allDay ? -1 : 1
      } else if (a.allDay) {
        return a.event.title.localeCompare(b.event.title)
      } else {
        return compareTimestamps(a.timestamp, b.timestamp)
      }
    })
    const timeZones = uniq(items.map((item) => item.timeZone))
    if (timeZones.length === 0) {
      timeZones.push(userTimeZone)
    }

    const type =
      hasEventsBeforeTrip && isFirstDay
        ? "before"
        : hasEventsAfterTrip && isLastDay
          ? "after"
          : "normal"
    if (type === "normal") {
      return {
        day,
        dayNumber: dayIndex + (hasEventsBeforeTrip ? 0 : 1),
        title,
        note,
        items,
        spanningHotelEvents,
        timeZones,
        type,
      }
    } else {
      return {
        day,
        title,
        note,
        items,
        spanningHotelEvents,
        timeZones,
        type,
      }
    }
  })
}

export function getActiveItineraryDayToScrollTo({
  itineraryDays,
  collapsedDays,
  timeFrame,
  timeZone,
  highlightedEventId,
  formatDayKey,
}: {
  itineraryDays: ExpandedItineraryDay[]
  collapsedDays: Record<string, boolean>
  timeFrame: TimeFrame
  timeZone: string
  highlightedEventId?: ItineraryEvent["id"]
  formatDayKey: (day: DayObject) => string
}): ExpandedItineraryDay | undefined {
  if (highlightedEventId) {
    return undefined
  }

  if (!isTimeFrameCurrentlyActive(timeFrame, timeZone)) {
    return undefined
  }

  const todayInTripTimeZone = today(timeZone)

  const hasCollapsedPastDay = itineraryDays.some((itineraryDay) => {
    if (!beforeDayObject(itineraryDay.day, todayInTripTimeZone)) {
      return false
    }

    const dayKey = formatDayKey(itineraryDay.day)
    return collapsedDays[dayKey] !== false
  })

  if (!hasCollapsedPastDay) {
    return undefined
  }

  const todayDay = itineraryDays.find(
    (day) => day.type === "normal" && sameDay(day.day, todayInTripTimeZone)
  )

  if (!todayDay || todayDay.type !== "normal") {
    return undefined
  }

  if (todayDay.dayNumber === 1) {
    return undefined
  }

  return todayDay
}

export function getItineraryActiveDayScrollTop({
  dayOffsetFromContainerTop,
  containerScrollTop,
  ideasDrawerHeight,
  ideasDrawerStickyTop = 0,
  fallbackScrollPaddingTop = 0,
}: {
  dayOffsetFromContainerTop: number
  containerScrollTop: number
  ideasDrawerHeight?: number
  ideasDrawerStickyTop?: number
  fallbackScrollPaddingTop?: number
}): number {
  const hasIdeasDrawer = ideasDrawerHeight != null && ideasDrawerHeight > 0
  const stickyOffset = hasIdeasDrawer
    ? ideasDrawerStickyTop + ideasDrawerHeight
    : fallbackScrollPaddingTop

  return Math.max(
    0,
    containerScrollTop + dayOffsetFromContainerTop - stickyOffset
  )
}

export function filterScheduledEvents(
  events: ItineraryEvent[]
): ItineraryEvent[] {
  return events.filter((e) => e.scheduled !== false)
}

export function filterUnscheduledEvents(
  events: ItineraryEvent[]
): ItineraryEvent[] {
  return events.filter((e) => e.scheduled === false)
}

const DEFAULT_UPCOMING_GRACE_HOURS = 6

export function upcomingScheduledEvents(
  events: ItineraryEvent[],
  options?: { limit?: number; graceHours?: number; nowMs?: number }
): ItineraryEvent[] {
  const limit = options?.limit ?? 5
  const graceHours = options?.graceHours ?? DEFAULT_UPCOMING_GRACE_HOURS
  const nowMs = options?.nowMs ?? nowMillis()
  const cutoffMs = nowMs - graceHours * 60 * 60 * 1000

  return filterScheduledEvents(events)
    .filter((event) => event.start_at.epoch_millis >= cutoffMs)
    .sort(compareStartTimes)
    .slice(0, limit)
}

export function itineraryEventDirectionsAddress(
  event: ItineraryEvent
): string | undefined {
  const addressParts = [
    event.properties.address,
    event.properties.city,
    event.properties.state,
    event.properties.country,
  ].filter(Boolean)

  if (addressParts.length > 0) {
    return addressParts.join(", ")
  }

  const { latitude, longitude } = event.properties
  if (latitude != null && longitude != null) {
    return `${latitude},${longitude}`
  }

  return undefined
}

export function itineraryEventDirectionsLink(
  event: ItineraryEvent
): string | undefined {
  if (event.place) {
    return googleDirectionsLink(event.place)
  }

  const address = itineraryEventDirectionsAddress(event)
  if (address) {
    return googleDirectionsLinkFromText(address)
  }

  return undefined
}
